import React, { useEffect, useRef } from 'react'
import { useS4LContext } from '@s4l/react-context'
import { useUseCase } from '@s4l/utils/hooks/useUseCase'

import Rooms from 'client/components/Rooms'
import { RoomsUpdater } from 'client/components/SearchForm'
import type { PropertyAPIResponse as PropertyType } from 'src/types/property'
import { usePage } from 'src/hooks/usePage'
import { useKafkaEvent } from 'src/hooks/useKafkaEvent'
import { SECTIONS } from 'src/hooks/useKafkaEvent/constants'

import styles from './styles.module.css'
import { RelatedProperties } from '@s4l/uikit'
import useExperiment from 'src/hooks/useExperimentConfig'
import { useEffectOnce } from 'react-use'
import debug from 'debug'

const log = debug('s4l:stayforlong_com:RoomSection')

const PRICE_DETAILS_EXPERIMENT_KEY = 'WEB-5637'

interface IRoomSection {
  readonly property: PropertyType
  readonly handleScroll: () => void
  readonly searchDataUpdated: boolean
  readonly source: string
  readonly isFetchingRooms: boolean
}

export default function RoomSection(props: IRoomSection) {
  const { property, handleScroll, searchDataUpdated, source, isFetchingRooms } = props
  const { i18n } = useS4LContext()
  const { pageId } = usePage()
  const { sendEvent: sendKafkaClick } = useKafkaEvent({ page: pageId })

  const { notify } = useExperiment(PRICE_DETAILS_EXPERIMENT_KEY)
  useEffectOnce(notify)

  const previousIsFetchingRooms = useRef(isFetchingRooms)

  const {
    data: relatedProperties,
    loading: isApiLoading,
    error: relatedError,
    execute,
  } = useUseCase(
    'GetRelatedPropertyUseCase',
    {
      id: property.id.toString(),
    },
    { lazy: true }
  )

  if (relatedError) {
    log('Error on GetRelatedPropertyUseCase')
  }

  useEffect(() => {
    if (previousIsFetchingRooms.current === true && isFetchingRooms === false) {
      execute()
    }
    previousIsFetchingRooms.current = isFetchingRooms
  }, [isFetchingRooms]) // eslint-disable-line react-hooks/exhaustive-deps

  const showLoadingState = isFetchingRooms || isApiLoading

  function handleKafkaClickCTA(id: string, position: number) {
    sendKafkaClick({ section: SECTIONS.RELATED }, { id_property: parseInt(id), pos: position })
  }

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{i18n.formatMessage({ id: 'global.rooms-available' })}</h2>
      <RoomsUpdater handleUpdate={handleScroll} />
      {searchDataUpdated && (
        <>
          <Rooms property={property} source={source} />
          <RelatedProperties
            relatedPropertiesJSON={relatedProperties?.toJSON()}
            loading={showLoadingState}
            onClickProperty={handleKafkaClickCTA}
          />
        </>
      )}
    </div>
  )
}
